import { useState, useEffect, useCallback } from 'react';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { clientUserSchema, insertUserSchema } from '../../../shared/schema';

// Global flag to prevent duplicate fetches
let isFetchingUser = false;
let hasFetchedUser = false;
let isLoggingOut = false;

export function useAuth() {
  const [user, setUser] = useState<z.infer<typeof clientUserSchema> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [instanceId] = useState(() => Math.random().toString(36).substring(2, 9));

  console.log('🔧 [useAuth] Hook initialized with instance:', instanceId);



  const fetchUser = useCallback(async () => {
    console.log('🔄 [useAuth] fetchUser called');
    if (isFetchingUser || hasFetchedUser) {
      console.log('⏭️ [useAuth] Skipping fetch - already fetching or fetched');
      return;
    }
    isFetchingUser = true;
    
    // Add timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      console.error('⏰ [useAuth] fetchUser timeout - forcing completion');
      isFetchingUser = false;
      hasFetchedUser = true;
      setLoading(false);
    }, 10000); // 10 second timeout
    
    try {
      console.log('🔍 [useAuth] Getting current session...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('❌ [useAuth] No session found');
        setUser(null);
        setError(null);
        return;
      }
      
      console.log('✅ [useAuth] Session found');
      console.log('👤 [useAuth] Session user ID:', session.user.id);
      console.log('📧 [useAuth] Session user email:', session.user.email);
      
      console.log('🔍 [useAuth] Fetching user data from database...');
      const { data: userData, error: dbError } = await supabase
        .from('users')
        .select('id, name, email, role, bio, city, avatar, phone')
        .eq('id', session.user.id)
        .maybeSingle();

      if (dbError) {
        console.error('❌ [useAuth] Database error:', dbError);
        throw dbError;
      }

      if (!userData) {
        console.log('❌ [useAuth] No user profile found in database');
        setUser(null);
        setError('No user profile found');
        return;
      }
      
      console.log('✅ [useAuth] User data fetched from database');
      console.log('👤 [useAuth] Database user ID:', userData.id);
      console.log('📧 [useAuth] Database user email:', userData.email);
      
      // Check for email mismatch
      if (session.user.email !== userData.email) {
        console.warn('⚠️ [useAuth] EMAIL MISMATCH DETECTED!');
        console.warn('📧 [useAuth] Auth email:', session.user.email);
        console.warn('📧 [useAuth] Database email:', userData.email);
        
        // Sync email from auth.users to public.users
        console.log('🔄 [useAuth] Syncing email to database...');
        const { error: syncError } = await supabase
          .from('users')
          .update({ email: session.user.email })
          .eq('id', session.user.id);
        
        if (syncError) {
          console.error('❌ [useAuth] Failed to sync email:', syncError);
        } else {
          console.log('✅ [useAuth] Successfully synced email to database');
          // Use the synced email for userData
          userData.email = session.user.email;
        }
      } else {
        console.log('✅ [useAuth] Email matches between auth and database');
      }
      
      const validatedData = clientUserSchema.parse(userData);
      console.log('✅ [useAuth] User data validated and set');
      setUser(validatedData);
      setError(null);
    } catch (err: any) {
      console.error('❌ [useAuth] Error in fetchUser:', err);
      setUser(null);
      setError(err.message || 'Failed to fetch user profile');
    } finally {
      clearTimeout(timeoutId);
      isFetchingUser = false;
      hasFetchedUser = true;
      setLoading(false);
      console.log('🏁 [useAuth] fetchUser completed');
    }
  }, [instanceId]);

  useEffect(() => {
    let isMounted = true;

    if (!hasFetchedUser && !isFetchingUser && isMounted) {
      fetchUser();
    }

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      if (event === 'SIGNED_IN' && session) {
        hasFetchedUser = false;
        await fetchUser();
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setError(null);
        setLoading(false);
        hasFetchedUser = false;
      } else if (event === 'INITIAL_SESSION' && !session) {
        if (!hasFetchedUser) {
          setLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      if (authListener?.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, [fetchUser]);

  const register = async (
    name: string,
    email: string,
    password: string,
    role: 'client' | 'agent' = 'client',
    phone?: string,
    address?: { street: string; city: string; state: string; zipCode: string }
  ) => {
    let validatedData;
    try {
      setError(null);

      try {


        validatedData = insertUserSchema.parse({
          name,
          email,
          password,
          role,
          phone,
          address,
        });

      } catch (validationError: any) {
        if (validationError instanceof z.ZodError) {
          // Find the first error and use its message
          if (validationError.errors.length > 0) {
            const firstError = validationError.errors[0];
            throw new Error(firstError.message);
          }
        }
        throw new Error(validationError.message || 'Invalid input data');
      }

      
      // Use server endpoint for registration to get proper email validation
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          password,
          role,
          phone: phone || '',
        }),
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          // Create an error object that preserves the original message
          const error = new Error(errorData.message || 'Registration failed');
          // Add the original error data as a property for debugging
          (error as any).originalError = errorData;
          throw error;
        } catch (jsonError) {
          // Only show connection error if we actually can't parse JSON
          if (jsonError instanceof SyntaxError) {
            throw new Error('Unable to connect to server. Please check your connection and try again.');
          }
          // Re-throw the original error if it's not a JSON parsing error
          throw jsonError;
        }
      }

      const userData = await response.json();
      
      // Set the session in Supabase client
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: userData.accessToken,
        refresh_token: '', // We'll let Supabase handle refresh
      });

      if (sessionError) {
        throw new Error('Failed to set authentication session');
      }

      // User data is already inserted by the server, just set local state
      setUser({
        id: userData.id,
        name: userData.name,
        email: userData.email,
        role: userData.role,
        phone: userData.phone || '',
        bio: '',
        city: '',
        avatar: '',
        fcmToken: userData.fcmToken || '',
        notificationPreferences: userData.notificationPreferences || {
          transactionUpdates: true,
          appointments: true,
          messages: true,
        },
        accessToken: userData.accessToken,
      });

      hasFetchedUser = false;
      await fetchUser();

      return true;
    } catch (error: any) {
      setError(error.message || 'Failed to register user');
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      const loginData = clientUserSchema
        .pick({ email: true })
        .extend({ password: z.string().min(10, 'Password must be at least 10 characters') })
        .parse({ email, password });

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError(authError.message || 'The email or password you entered is incorrect. Please try again.');
        return null;
      }

      if (!data.session) {
        setError('Failed to create session. Please try again.');
        return null;
      }



      hasFetchedUser = false;
      await fetchUser();

      return user;
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during login. Please try again.');
      return null;
    }
  };

  const logout = async () => {
    if (isLoggingOut) {
      return; // Prevent multiple simultaneous logout attempts
    }
    
    isLoggingOut = true;
    try {
      setError(null);
      
      // Clear recovery flags FIRST to prevent any redirects during logout
      try {
        localStorage.removeItem('recovery_handled');
        localStorage.removeItem('recovery_tokens_present');
        sessionStorage.removeItem('suppress_reset_init');
        // Clear any other potential recovery-related flags
        localStorage.removeItem('recovery_session_established');
        
        // Also clear any URL hash/search that might contain recovery tokens
        // Do NOT mutate URL during logout; this can race with iOS deep link handling
        // We only clear local/session storage flags above; URL will be handled by deep link code
      } catch (error) {
        // Ignore errors when clearing recovery flags
      }
      
      // Call server logout
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Logout failed');
      }

      // Clear Supabase session
      await supabase.auth.signOut();
      
      // Clear local state
      setUser(null);
      setError(null);
      hasFetchedUser = false;
      
      // Clear any cached data
      localStorage.removeItem('favorites');
      
    } catch (err: any) {
      // Even if server logout fails, clear local state
      setUser(null);
      setError(null);
      hasFetchedUser = false;
      localStorage.removeItem('favorites');
      
      // Clear recovery flags to allow fresh password resets
      try {
        localStorage.removeItem('recovery_handled');
        localStorage.removeItem('recovery_tokens_present');
        sessionStorage.removeItem('suppress_reset_init');
        // Clear any other potential recovery-related flags
        localStorage.removeItem('recovery_session_established');
      } catch (error) {
        // Ignore errors when clearing recovery flags
      }
      
      // Try to clear Supabase session anyway
      try {
        await supabase.auth.signOut();
      } catch (supabaseError) {
        // Ignore Supabase errors during cleanup
      }
      
      setError(err.message || 'Logout failed');
      throw err;
    } finally {
      isLoggingOut = false;
    }
  };


  const clearError = () => setError(null);

  return {
    user,
    register,
    login,
    logout,
    loading,
    error,
    fetchUser,
    clearError,
    setUser,
  };
}